import { FreeMode } from 'swiper/modules'

import ParkinglotShareBottomSheet from '@/components/detail/components/shared/parkinglotShare/view'
import RecommendParkinglotView from '@/components/recommendParkinglot/view'
import { useRecommendParkinglotViewModel } from '@/components/recommendParkinglot/viewModel'
import Location from '@/components/ticketDetail/components/parkinglot/components/Location'
import TicketInfo from '@/components/ticketDetail/components/parkinglot/components/TicketInfo'
import useParkingTicketDetailViewModel from '@/components/ticketDetail/components/parkinglot/viewModel'
import Header from '@/components/ticketDetail/components/shared/Header'
import Notice from '@/components/ticketDetail/components/shared/Notice'
import ParkinglotPhotoList from '@/components/ticketDetail/components/shared/parkinglotPhoto/List'
import PurchaseButton from '@/components/ticketDetail/components/shared/PurchaseButton'
import TabBar from '@/components/ticketDetail/components/shared/TabBar'

import type { Ticket } from '@/api/dto/ticket'
import type { ParkinglotDetail, SharedParkinglotDetail, TicketsItem } from '@/api/server/detail/dto/detailDto'
import Footer from '@/shared/components/Footer'
import type { SliderOptions } from '@/shared/components/slider/types/sliderOptions'
import Toast from '@/shared/components/Toast'
import type { Dispatch, SetStateAction } from 'react'

interface ParkingTicketDetailViewProps {
  ticket: Ticket
  setTicket: Dispatch<SetStateAction<Ticket>>
  tickets: TicketsItem[]
  pin: ParkinglotDetail | SharedParkinglotDetail
  geoCodeNeighborsWithSelf: string
  setIsRoadViewOpen: Dispatch<SetStateAction<boolean>>
  setIsDetailMapOpen: Dispatch<SetStateAction<boolean>>
}

const ParkingTicketDetailView = ({
  ticket,
  setTicket,
  tickets,
  pin,
  geoCodeNeighborsWithSelf,
  setIsRoadViewOpen,
  setIsDetailMapOpen
}: ParkingTicketDetailViewProps) => {
  const { states, handlers, texts, setters } = useParkingTicketDetailViewModel({ ticket, setTicket, tickets, pin })

  const currentParkinglotPosition = {
    lat: ticket.parkinglot.parkinglotLatitude,
    lng: ticket.parkinglot.parkinglotLongitude
  }
  const { states: RPLstates } = useRecommendParkinglotViewModel({
    currentParkinglotPosition,
    id: String(ticket.parkinglot.parkinglotSeq),
    geoCodeNeighborsWithSelf
  })

  const headerProps = {
    title: texts.pageTitle,
    onBackClick: handlers.handleBackClick,
    onShareClick: () => setters.setIsShareOpen(true)
  }

  const tabBarProps = {
    tabs: states.tabs,
    selectedSeq: states.ticketSeq,
    onClick: handlers.handleTabClick
  }

  const parkinglotPhotos = {
    items: states.parkinglotPhotos,
    options: {
      fullScreen: true,
      isFreeMode: true,
      swiperModules: [FreeMode]
    } satisfies SliderOptions
  }

  const noticeProps = {
    title: texts.noticeTitle,
    text: states.notice
  }

  const notice2Props = {
    title: texts.notice2Title,
    text: states.notice2
  }

  const enteringNoticeProps = {
    title: texts.enteringNoticeTitle,
    text: states.enteringNotice
  }

  const locationProps = {
    ...states.location,
    onClick: () => setIsRoadViewOpen(true),
    onCopy: handlers.handleCopyAddress,
    setIsDetailMapOpen: setIsDetailMapOpen
  }

  const recommendParkinglotsProps = {
    sortedParkinglots: RPLstates.sortedParkinglots
  }

  const shareBottomSheetProps = {
    isOpen: states.isShareOpen,
    onClose: () => setters.setIsShareOpen(false),
    type: 't',
    seq: states.ticket?.couponSeq || ticket.couponSeq,
    parkinglotName: pin.basic.name,
    parkinglotAddress: ticket.parkinglot.parkinglotAddress,
    targetId: '#layer2',
    price: ticket.price,
    photo: ticket.photos[0]
  }

  const toastProps = {
    text: states.isCopyFail ? '주소 복사에 실패했습니다.' : '주차장 주소가 복사됐습니다.',
    onClose: () => setters.setIsToastOpen(false)
  }

  const purchaseButtonProps = {
    ...states.purchaseButtonProps,
    onClick: handlers.handleClickPurchase
  }

  return (
    <div>
      <div className="absolute top-0 h-[184px] w-full bg-main-050" />
      <div className="relative">
        <Header {...headerProps} />
        <TabBar {...tabBarProps} />
        <TicketInfo {...states.ticketInfo} />
        {states.parkinglotPhotos.length > 0 && <ParkinglotPhotoList {...parkinglotPhotos} />}
        {notice2Props.text && <Notice {...notice2Props} />}
        {enteringNoticeProps.text && <Notice {...enteringNoticeProps} />}
        {noticeProps.text && <Notice {...noticeProps} />}
        <Location {...locationProps} />
        {RPLstates.sortedParkinglots.length > 0 && <RecommendParkinglotView {...recommendParkinglotsProps} />}
      </div>
      <Footer />
      {states.isToastOpen && <Toast {...toastProps} />}
      <ParkinglotShareBottomSheet {...shareBottomSheetProps} />
      <PurchaseButton {...purchaseButtonProps} />
    </div>
  )
}

export default ParkingTicketDetailView
